import { Select, type SelectItem } from '@vrplatform/components';
import { IconButton } from '@vrplatform/components-legacy';
import { type DataRegion, usePartnerRegion } from '@vrplatform/data';
import { ChevronIcon, Icon } from '@vrplatform/icons';
import { Box, Group, Stack, Text } from '@mantine/core';
import { forwardRef, useMemo } from 'react';
import { useNavbarExpanded } from '../hooks';

const REGION_LABELS: Record<DataRegion, string> = {
  ap: 'AP',
  crunchy: 'Legacy',
  eu: 'EU',
  hostaway: 'Hostaway',
  us: 'US',
};

export const PartnerRegionSwitch = () => {
  const { managedTeamRegions, selectedDataRegion, setSelectedDataRegion } =
    usePartnerRegion();
  const { isDesktopExpanded } = useNavbarExpanded();
  const options = useMemo<SelectItem<DataRegion>[]>(
    () =>
      managedTeamRegions.map((region) => ({
        value: region.dataRegion,
        label: REGION_LABELS[region.dataRegion],
        description: `${region.counts.active} active, ${region.counts.inactive} inactive`,
      })),
    [managedTeamRegions]
  );
  const value =
    options.find((option) => option.value === selectedDataRegion) ?? null;

  if (managedTeamRegions.length < 2 || !value) return null;

  return (
    <Box
      sx={(theme) => ({
        borderBottom: '1px solid #ffffff20',
        paddingBottom: theme.spacing.sm,
      })}
    >
      <Stack gap={0} sx={{ color: '#fff' }}>
        {isDesktopExpanded && (
          <Text ml={5} sx={{ opacity: 0.8 }} size="xs">
            Data region
          </Text>
        )}
        <Select
          name="Data region"
          type="single"
          setValue={(nextValue) => {
            if (nextValue) {
              void setSelectedDataRegion(nextValue.value);
            }
          }}
          data={{ options }}
          dropdownProps={{
            hideSearch: true,
            position: isDesktopExpanded ? 'bottom' : 'bottom-start',
            width: 260,
            withinPortal: true,
          }}
          value={value}
        >
          {({ value }) => (
            <RegionSwitchTrigger
              isNavigationExpanded={isDesktopExpanded}
              label={Array.isArray(value) ? undefined : value?.label}
            />
          )}
        </Select>
      </Stack>
    </Box>
  );
};

const RegionSwitchTrigger = forwardRef<
  HTMLDivElement,
  { isNavigationExpanded: boolean; label?: string }
>(({ isNavigationExpanded, label }, ref) => (
  <Group
    ref={ref}
    wrap="nowrap"
    justify={isNavigationExpanded ? 'space-between' : 'center'}
    gap={isNavigationExpanded ? 0 : 4}
    pb={4}
    sx={{
      borderRadius: 7,
      cursor: 'pointer',
      maxWidth: 255,
      ':hover': {
        backgroundColor: '#5C617840',
      },
    }}
  >
    <Group gap={5} wrap="nowrap" ml={isNavigationExpanded ? 5 : 0}>
      <Icon icon="Globe2Icon" size={17} color={(theme) => theme.white} />
      {isNavigationExpanded && (
        <Text component="span" sx={{ lineHeight: 'normal' }}>
          {label}
        </Text>
      )}
    </Group>
    <IconButton
      component="span"
      sx={{
        ':hover': {
          backgroundColor: 'transparent',
        },
      }}
    >
      <ChevronIcon size={17} color="#ffffff80" />
    </IconButton>
  </Group>
));
